import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import MinimalLayout from '../components/layout/MinimalLayout';
import VerificationBadge from '../components/VerificationBadge';
import { TeacherCardSkeleton } from '../components/LoadingSpinner';
import { 
  MagnifyingGlassIcon,
  FunnelIcon,
  MapPinIcon,
  StarIcon,
  ClockIcon,
  UserIcon,
  ChevronRightIcon,
  AdjustmentsHorizontalIcon
} from '@heroicons/react/24/solid';

interface StudentPreferences {
  instrument: string;
  genres: string[];
  skillLevel: string;
  lessonLocation: string[];
  age: string;
  learnerType: 'learner' | 'guardian';
  learnerFirstName?: string;
}

interface MockTeacher {
  id: string;
  name: string;
  instruments: string[];
  specialties?: string[];
  rating: number;
  totalReviews: number;
  hourlyRate: number;
  location: string;
  distance?: string;
  profilePicture?: string;
  bio: string;
  experience: string;
  availability?: string[];
  lessonFormats?: string[];
  isVerified?: boolean;
  verificationLevel?: 'basic' | 'verified';
}

// Mock teacher data - in a real app, this would come from an API
const mockTeachers: MockTeacher[] = [
  {
    id: '1',
    name: 'Sarah Johnson',
    instruments: ['Piano', 'Voice/Vocals'],
    specialties: ['Classical', 'Jazz', 'Pop'],
    rating: 4.9,
    totalReviews: 127,
    hourlyRate: 75,
    location: 'Downtown',
    distance: '2.5 miles',
    bio: 'Professional pianist and vocalist with 15+ years of teaching experience. Specializes in classical technique and contemporary performance.',
    experience: '15+ years',
    availability: ['Weekdays', 'Evenings'],
    lessonFormats: ['Online', 'In-home (teacher\'s studio)', 'In-home (student\'s home)'],
    isVerified: true,
    verificationLevel: 'verified'
  },
  {
    id: '2',
    name: 'Michael Chen',
    instruments: ['Guitar', 'Ukulele'],
    specialties: ['Rock', 'Blues', 'Folk'],
    rating: 4.8,
    totalReviews: 89,
    hourlyRate: 60,
    location: 'Midtown',
    distance: '4.1 miles',
    bio: 'Guitar instructor focused on building strong fundamentals while exploring creative expression across multiple genres.',
    experience: '10-15 years',
    availability: ['Weekends', 'Evenings'],
    lessonFormats: ['Online', 'In-home (teacher\'s studio)'],
    isVerified: false,
    verificationLevel: 'basic'
  },
  {
    id: '3',
    name: 'Emily Rodriguez',
    instruments: ['Violin', 'Piano'],
    specialties: ['Classical', 'Film Scores', 'Contemporary'],
    rating: 5.0,
    totalReviews: 56,
    hourlyRate: 85,
    location: 'Uptown',
    distance: '6.3 miles',
    bio: 'Classically trained violinist and pianist with experience in orchestral performance and private instruction.',
    experience: '10-15 years',
    availability: ['Weekdays', 'Weekends'],
    lessonFormats: ['Online', 'In-home (teacher\'s studio)', 'In-home (student\'s home)'],
    isVerified: true,
    verificationLevel: 'verified'
  },
  {
    id: '4',
    name: 'David Wilson',
    instruments: ['Drums', 'Percussion'],
    specialties: ['Rock', 'Jazz', 'Latin'],
    rating: 4.7,
    totalReviews: 134,
    hourlyRate: 70,
    location: 'Westside',
    distance: '3.8 miles',
    bio: 'Professional drummer with touring and studio experience. Teaches all skill levels with focus on rhythm, technique, and musical expression.',
    experience: '15+ years',
    availability: ['Weekdays', 'Evenings', 'Weekends'],
    lessonFormats: ['In-home (teacher\'s studio)', 'In-home (student\'s home)'],
    isVerified: false,
    verificationLevel: 'basic'
  }
];

const BrowseTeachersPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  
  const studentPreferences = location.state?.studentPreferences as StudentPreferences;
  const [teachers, setTeachers] = useState<MockTeacher[]>([]);
  const [filteredTeachers, setFilteredTeachers] = useState<MockTeacher[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    maxPrice: 100,
    minRating: 0,
    lessonFormat: 'all',
    genre: 'all',
    availability: 'all',
    experience: 'all'
  });
  const [sortBy, setSortBy] = useState('rating'); // rating, price, reviews, distance

  // Log student preferences for debugging
  console.log('Browse teachers page loaded with preferences:', studentPreferences);

  // Simulate loading teachers data
  useEffect(() => {
    console.log('Loading teachers data...');
    setTimeout(() => {
      setTeachers(mockTeachers);
      setLoading(false);
    }, 1200); // Simulate API call
  }, []);

  useEffect(() => {
    if (loading) return;
    // Auto-filter based on student preferences if available
    if (studentPreferences) {
      let filtered = teachers;
      
      // Filter by instrument
      if (studentPreferences.instrument && studentPreferences.instrument !== 'music') {
        filtered = filtered.filter(teacher => 
          (teacher.instruments || []).some(inst => 
            inst.toLowerCase().includes(studentPreferences.instrument.toLowerCase())
          )
        );
      }
      
      // Filter by genres
      if (studentPreferences.genres && studentPreferences.genres.length > 0) {
        filtered = filtered.filter(teacher =>
          (teacher.specialties || []).some(specialty =>
            (studentPreferences.genres || []).some(genre =>
              specialty.toLowerCase().includes(genre.toLowerCase())
            )
          )
        );
      }
      
      setFilteredTeachers(filtered);
    }
  }, [studentPreferences, teachers]);

  // Re-apply filters when sort changes
  useEffect(() => {
    if (!loading && teachers.length > 0) {
      applyFilters(searchTerm, filters);
    }
  }, [sortBy]);

  const applyFilters = (searchTerm: string = '', currentFilters = filters) => {
    let filtered = teachers;

    // Text search
    if (searchTerm.trim()) {
      filtered = filtered.filter(teacher =>
        teacher.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (teacher.instruments || []).some(inst => inst.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (teacher.specialties || []).some(spec => spec.toLowerCase().includes(searchTerm.toLowerCase())) ||
        teacher.location.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Price filter
    if (currentFilters.maxPrice < 100) {
      filtered = filtered.filter(teacher => teacher.hourlyRate <= currentFilters.maxPrice);
    }

    // Rating filter
    if (currentFilters.minRating > 0) {
      filtered = filtered.filter(teacher => teacher.rating >= currentFilters.minRating);
    }

    // Lesson format filter
    if (currentFilters.lessonFormat !== 'all') {
      filtered = filtered.filter(teacher =>
        (teacher.lessonFormats || []).some(format =>
          format.toLowerCase().includes(currentFilters.lessonFormat.toLowerCase())
        )
      );
    }

    // Genre filter
    if (currentFilters.genre !== 'all') {
      filtered = filtered.filter(teacher =>
        (teacher.specialties || []).some(specialty =>
          specialty.toLowerCase().includes(currentFilters.genre.toLowerCase())
        )
      );
    }

    // Experience filter
    if (currentFilters.experience !== 'all') {
      filtered = filtered.filter(teacher => {
        const exp = teacher.experience.toLowerCase();
        switch (currentFilters.experience) {
          case 'beginner': return exp.includes('5') || exp.includes('new');
          case 'intermediate': return exp.includes('5-10') || exp.includes('10');
          case 'expert': return exp.includes('15+') || exp.includes('20+');
          default: return true;
        }
      });
    }

    // Apply sorting
    const sortedFiltered = [...filtered].sort((a, b) => {
      switch (sortBy) {
        case 'rating':
          return b.rating - a.rating;
        case 'price':
          return a.hourlyRate - b.hourlyRate;
        case 'reviews':
          return b.totalReviews - a.totalReviews;
        case 'distance':
          const aDistance = parseFloat(a.distance?.split(' ')[0] || '0');
          const bDistance = parseFloat(b.distance?.split(' ')[0] || '0');
          return aDistance - bDistance;
        default:
          return 0;
      }
    });

    setFilteredTeachers(sortedFiltered);
  };

  const handleSearch = (term: string) => {
    setSearchTerm(term);
    applyFilters(term, filters);
  };

  const handleFilterChange = (newFilters: typeof filters) => {
    setFilters(newFilters);
    applyFilters(searchTerm, newFilters);
  };

  const handleSortChange = (newSortBy: string) => {
    setSortBy(newSortBy);
    applyFilters(searchTerm, filters);
  };

  const handleTeacherSelect = (teacher: MockTeacher) => {
    console.log('Selected teacher:', teacher);
    // TODO: Navigate to teacher detail page or booking flow
    navigate('/teacher-detail', { 
      state: { 
        teacher,
        studentPreferences 
      } 
    });
  };

  const renderStars = (rating: number) => {
    return (
      <div className="flex items-center space-x-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <StarIcon
            key={star}
            className={`w-4 h-4 ${star <= rating ? 'text-yellow-400' : 'text-gray-300'}`}
          />
        ))}
      </div>
    );
  };

  const isGuardian = studentPreferences?.learnerType === 'guardian';
  const learnerName = studentPreferences?.learnerFirstName || 'you';

  return (
    <MinimalLayout noPadding>
      <div className="min-h-screen bg-gray-50">
        
        {/* Header */}
        <div className="bg-white shadow-sm border-b">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 lg:py-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
              <div>
                <h1 className="text-2xl lg:text-3xl font-bold text-gray-900">
                  Find {studentPreferences?.instrument || 'Music'} Teachers
                </h1>
                {studentPreferences && (
                  <p className="text-gray-600 mt-1 text-sm lg:text-base">
                    {isGuardian ? `Finding teachers for ${learnerName}` : 'Based on your preferences'}
                  </p>
                )}
              </div>
              <button
                onClick={() => navigate('/student-profile', { state: { studentData: studentPreferences } })}
                className="text-blue-600 hover:text-blue-700 font-medium text-sm lg:text-base self-start sm:self-auto"
              >
                Back to Profile
              </button>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 lg:py-8">
          
          {/* Search and Filters */}
          <div className="mb-8">
            <div className="flex flex-col sm:flex-row gap-4 mb-4">
              {/* Search Bar */}
              <div className="flex-1 relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search teachers by name, instrument, or genre..."
                  value={searchTerm}
                  onChange={(e) => handleSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              
              {/* Filter Toggle */}
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center space-x-2 px-4 py-3 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                <FunnelIcon className="w-5 h-5" />
                <span>Filters</span>
              </button>
            </div>

            {/* Advanced Filters */}
            {showFilters && (
              <div className="bg-white rounded-lg border border-gray-200 p-4 mb-4">
                <h3 className="font-medium text-gray-900 mb-4">Advanced Filters</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {/* Price Range */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Max Price: ${filters.maxPrice}/hour
                    </label>
                    <input
                      type="range"
                      min="25"
                      max="150"
                      value={filters.maxPrice}
                      onChange={(e) => handleFilterChange({ ...filters, maxPrice: parseInt(e.target.value) })}
                      className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                    />
                    <div className="flex justify-between text-xs text-gray-500 mt-1">
                      <span>$25</span>
                      <span>$150+</span>
                    </div>
                  </div>

                  {/* Minimum Rating */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Minimum Rating
                    </label>
                    <select
                      value={filters.minRating}
                      onChange={(e) => handleFilterChange({ ...filters, minRating: parseFloat(e.target.value) })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value={0}>Any Rating</option>
                      <option value={4.5}>4.5+ Stars</option>
                      <option value={4.0}>4.0+ Stars</option>
                      <option value={3.5}>3.5+ Stars</option>
                    </select>
                  </div>

                  {/* Lesson Format */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Lesson Format
                    </label>
                    <select
                      value={filters.lessonFormat}
                      onChange={(e) => handleFilterChange({ ...filters, lessonFormat: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="all">All Formats</option>
                      <option value="online">Online Only</option>
                      <option value="in-home">In-Person Only</option>
                      <option value="studio">Teacher's Studio</option>
                    </select>
                  </div>

                  {/* Genre */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Genre Specialty
                    </label>
                    <select
                      value={filters.genre}
                      onChange={(e) => handleFilterChange({ ...filters, genre: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="all">All Genres</option>
                      <option value="classical">Classical</option>
                      <option value="jazz">Jazz</option>
                      <option value="rock">Rock</option>
                      <option value="pop">Pop</option>
                      <option value="blues">Blues</option>
                      <option value="folk">Folk</option>
                    </select>
                  </div>

                  {/* Experience Level */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Experience Level
                    </label>
                    <select
                      value={filters.experience}
                      onChange={(e) => handleFilterChange({ ...filters, experience: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="all">All Experience</option>
                      <option value="beginner">New Teachers (0-5 years)</option>
                      <option value="intermediate">Experienced (5-15 years)</option>
                      <option value="expert">Expert (15+ years)</option>
                    </select>
                  </div>

                  {/* Clear Filters */}
                  <div className="flex items-end">
                    <button
                      onClick={() => {
                        const defaultFilters = {
                          maxPrice: 100,
                          minRating: 0,
                          lessonFormat: 'all',
                          genre: 'all',
                          availability: 'all',
                          experience: 'all'
                        };
                        handleFilterChange(defaultFilters);
                      }}
                      className="w-full px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      Clear Filters
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Preference Summary */}
            {studentPreferences && (
              <div className="bg-blue-50 rounded-lg p-4 mb-4">
                <h3 className="font-medium text-blue-900 mb-2">
                  {isGuardian ? `${learnerName}'s Preferences` : 'Your Preferences'}
                </h3>
                <div className="flex flex-wrap gap-2">
                  <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm">
                    {studentPreferences.instrument}
                  </span>
                  <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm">
                    {studentPreferences.skillLevel}
                  </span>
                  {(studentPreferences.genres || []).slice(0, 3).map((genre, index) => (
                    <span key={index} className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm">
                      {genre}
                    </span>
                  ))}
                  {(studentPreferences.genres || []).length > 3 && (
                    <span className="text-blue-700 text-sm px-3 py-1">
                      +{(studentPreferences.genres || []).length - 3} more
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Results Count and Sort */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 space-y-3 sm:space-y-0">
            <p className="text-gray-600">
              {filteredTeachers.length} teacher{filteredTeachers.length !== 1 ? 's' : ''} found
            </p>
            <div className="flex items-center space-x-2">
              <AdjustmentsHorizontalIcon className="w-5 h-5 text-gray-400" />
              <span className="text-sm text-gray-600">Sort by:</span>
              <select
                value={sortBy}
                onChange={(e) => handleSortChange(e.target.value)}
                className="px-3 py-1 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="rating">Highest Rated</option>
                <option value="price">Lowest Price</option>
                <option value="reviews">Most Reviews</option>
                <option value="distance">Closest Distance</option>
              </select>
            </div>
          </div>

          {/* Teachers Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
            {loading ? (
              // Show skeleton loading cards
              Array.from({ length: 6 }).map((_, index) => (
                <TeacherCardSkeleton key={index} />
              ))
            ) : (
              filteredTeachers.map((teacher) => (
              <div
                key={teacher.id}
                onClick={() => handleTeacherSelect(teacher)}
                className="bg-white rounded-xl shadow-md hover:shadow-lg transition-shadow cursor-pointer border border-gray-200"
              >
                {/* Teacher Card Header */}
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center">
                        <UserIcon className="w-6 h-6 text-gray-500" />
                      </div>
                      <div>
                        <div className="flex items-center space-x-2">
                          <h3 className="font-semibold text-gray-900">{teacher.name}</h3>
                          <VerificationBadge 
                            isVerified={teacher.isVerified || false} 
                            size="sm" 
                            showTooltip={true}
                          />
                        </div>
                        <p className="text-sm text-gray-600">{teacher.experience}</p>
                      </div>
                    </div>
                    <ChevronRightIcon className="w-5 h-5 text-gray-400" />
                  </div>

                  {/* Rating and Reviews */}
                  <div className="flex items-center space-x-2 mb-3">
                    {renderStars(teacher.rating)}
                    <span className="text-sm font-medium">{teacher.rating}</span>
                    <span className="text-sm text-gray-500">({teacher.totalReviews} reviews)</span>
                  </div>

                  {/* Instruments */}
                  <div className="mb-3">
                    <div className="flex flex-wrap gap-2">
                      {(teacher.instruments || []).map((instrument, index) => (
                        <span
                          key={index}
                          className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm"
                        >
                          {instrument}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Specialties */}
                  <div className="mb-4">
                    <p className="text-sm text-gray-600 mb-1">Specialties:</p>
                    <div className="flex flex-wrap gap-2">
                      {(teacher.specialties || []).slice(0, 3).map((specialty, index) => (
                        <span
                          key={index}
                          className="bg-blue-100 text-blue-700 px-2 py-1 rounded text-xs"
                        >
                          {specialty}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Location and Price */}
                  <div className="flex items-center justify-between text-sm text-gray-600 mb-4">
                    <div className="flex items-center space-x-1">
                      <MapPinIcon className="w-4 h-4" />
                      <span>{teacher.distance}</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <ClockIcon className="w-4 h-4" />
                      <span>${teacher.hourlyRate}/hour</span>
                    </div>
                  </div>

                  {/* Bio Preview */}
                  <p className="text-sm text-gray-600 line-clamp-2">
                    {teacher.bio}
                  </p>
                </div>
              </div>
              ))
            )}
          </div>

          {/* No Results */}
          {!loading && filteredTeachers.length === 0 && (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
                <MagnifyingGlassIcon className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No teachers found</h3>
              <p className="text-gray-600 mb-4">
                Try adjusting your search terms or filters to find more teachers.
              </p>
              <button
                onClick={() => {
                  setSearchTerm('');
                  setFilteredTeachers(teachers);
                }}
                className="text-blue-600 hover:text-blue-700 font-medium"
              >
                Clear search
              </button>
            </div>
          )}
        </div>
      </div>
    </MinimalLayout>
  );
};

export default BrowseTeachersPage; 